import {
  type ActionFunction,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from "@remix-run/node";
import {
  ClientActionFunction,
  ClientLoaderFunctionArgs,
  json,
  Outlet,
  useFetcher,
  useLoaderData,
  useLocation,
  useParams,
} from "@remix-run/react";
import { useContext, useEffect, useRef } from "react";
import {
  commitPersistentSession,
  commitSession,
  getAuthSession,
  getPersistentSession,
  getSession,
} from "~/sessions";
import invariant from "tiny-invariant";

import Api from "~/api/api";
import PageWrapper from "~/components/PageWrapper/PageWrapper";
import ThemeContext from "~/contexts/ThemeContext";
import { getActiveMenu } from "~/helpers/menuHelpers";
import BaseLayout from "~/layouts/BaseLayout/BaseLayout";
import { getServiceStatus } from "~/helpers/siteHelpers";
import { urlOrderSource } from "~/routes/$orderSource";
import { OrderContextProvider } from "~/contexts/OrderContext";
import { MenuContextProvider } from "~/contexts/MenuContext";
import { BannerContextProvider } from "~/contexts/BannerContext";
import { OnlineBanner } from "~/entities/bannerEntities";
import { TaxContextProvider } from "~/contexts/TaxContext";
import { SiteContextProvider } from "~/contexts/SiteContext";
import { SerializeFrom } from "@remix-run/server-runtime/dist/serialize";
import { PaymentContextProvider } from "~/contexts/PaymentContext";
import {
  cartToReq,
  OrderStatus,
  ResponseOrderWithCartLines,
  ResponseOrderWithLines,
} from "~/entities/orderEntities";
import {
  getJsonFormValue,
  getOptionalBoolFormValue,
  getOptionalJsonFormValue,
  getOptionalJSONNullStrFormValue,
  getOptionalStrFormValue,
  getStrFormValue,
} from "~/helpers/formHelpers";
import DefaultErrorBoundary from "~/components/DefaultErrorBoundary";
import AuthContext from "~/contexts/AuthContext";

import { makeAuthHeaders } from "~/api/endpoints/Auth";
import {
  getBestMatchingSlot,
  getOrderTypeCode,
  orderTypeCodeValid,
} from "~/helpers/orderHelper";
import { v4 } from "uuid";
import { iconMeta } from "~/helpers/remixHelpers";
import { ErrorType, isErrorType } from "~/modules/checkout/constants";
import { ShouldRevalidateFunctionArgs } from "@remix-run/router/utils";
import { isPaymentPage } from "~/helpers/routesHelpers";
import SignBackInModal from "~/modules/auth/containers/SignBackInModal/SignBackInModal";
import { isBefore } from "date-fns";

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const pageTitle = data?.siteInfo?.domainSettings?.title
    ? data?.siteInfo?.domainSettings?.title
    : data?.siteInfo?.site?.siteName;
  return [
    iconMeta(matches),
    {
      title: pageTitle
        ? `${pageTitle} - Product selection`
        : "Product selection",
    },
    {
      name: "description",
      content: data?.siteInfo?.domainSettings?.description
        ? `${data?.siteInfo?.domainSettings?.description} - Product selection page`
        : "Product selection page",
    },
  ];
};

export const action: ActionFunction = async ({ request, params }) => {
  const session = await getSession(request.headers.get("Cookie"));
  const authSession = await getAuthSession(request.headers.get("Cookie"));
  const persistentSession = await getPersistentSession(
    request.headers.get("Cookie"),
  );

  let sessionModified = false;
  let persistentSessionModified = false;

  const makeResponseHeaders = async (): Promise<[string, string][]> => {
    const headers: [string, string][] = [];
    if (sessionModified) {
      headers.push(["Set-Cookie", await commitSession(session)]);
    }
    if (persistentSessionModified) {
      headers.push([
        "Set-Cookie",
        await commitPersistentSession(persistentSession),
      ]);
    }
    return headers;
  };

  const authenticatedUserId = authSession.get("authenticatedUserId");
  const accessToken = authSession.get("accessToken");
  const authHeaders = makeAuthHeaders(request, accessToken);

  const formData = await request.formData();

  let orderId = session.get("orderId");
  const orgId = getStrFormValue(formData, "orgId");

  const saveCustomerDetails = getOptionalBoolFormValue(
    formData,
    "allowSavePersonalData",
  );

  invariant(params.orderSource, "params.orderSource expected");
  invariant(params.siteId, "params.siteId expected");

  // Set new requested time in session if provided
  const setRequestedTime = getOptionalJSONNullStrFormValue(
    formData,
    "setRequestedTime",
  );
  if (setRequestedTime !== undefined) {
    session.set("requestedReadyTime", setRequestedTime);
    sessionModified = true;
  }

  // Set new order type in session if provided
  const setOrderTypeCode = getOptionalStrFormValue(
    formData,
    "setOrderTypeCode",
  );
  if (setOrderTypeCode) {
    session.set("orderTypeCode", setOrderTypeCode);
    sessionModified = true;
  }

  // Set new delivery address in session if provided
  const setDeliveryInfo = getOptionalJsonFormValue(formData, "setDeliveryInfo");
  if (setDeliveryInfo !== undefined) {
    session.set("deliveryInfo", setDeliveryInfo);
    sessionModified = true;
  }

  const redirectToPayment = async () => {
    const url = `/${params.orderSource}/${params.siteId}${
      params.table ? "/" + params.table : ""
    }/payment`;

    return redirect(url, { headers: await makeResponseHeaders() });
  };

  if (getOptionalBoolFormValue(formData, "isCancelPayment") && orderId) {
    const paymentIds: string[] = getJsonFormValue(formData, "paymentIds");
    const siteId = params.siteId;
    const oId = orderId; // Needed so typescript doesn't complain about the value of orderId potentially changing
    try {
      await Promise.all(
        paymentIds.map((x) =>
          Api.Payment.cancelPayment(authHeaders, orgId, siteId, oId, x),
        ),
      );
    } catch {
      return await redirectToPayment();
    }
    session.unset("paymentId");
    sessionModified = true;
    return json(
      { success: true },
      {
        headers: await makeResponseHeaders(),
      },
    );
  }

  if (getOptionalBoolFormValue(formData, "isReset")) {
    session.unset("orderId");
    sessionModified = true;

    // If there is an error clearing the order, we still need to return success so that the user can start over with ease
    if (orderId) {
      try {
        await Api.Order.clearOrder(authHeaders, orgId, params.siteId, orderId);
      } catch (e) {
        console.warn("error clearing order", e);
      }
    }

    return json(
      { success: true },
      {
        headers: await makeResponseHeaders(),
      },
    );
  }

  if (formData.get("forceInsert")?.toString() && orderId) {
    orderId = undefined;
    // The code later will update with the new orderId on successful insert, so no need to modify session here
  }

  const orderSummaryFromForm = formData.get("orderSummary")?.toString();
  invariant(orderSummaryFromForm, "orderSummary is required");

  const orderSummary: ResponseOrderWithCartLines =
    JSON.parse(orderSummaryFromForm);

  // If response order id is not empty, but session order id is
  // e.g. if session expired so we lost order id reference,
  // or if we have requested a force insert
  // then generate new ids for all lines
  if (
    orderSummary.id != null &&
    orderSummary.id !== "" &&
    orderSummary.id !== orderId
  ) {
    console.log("Generating new ids for every line");
    for (const line of orderSummary.lines) {
      line.id = v4();
    }
  }

  const promises: Promise<any>[] = [];
  if (saveCustomerDetails) {
    if (authenticatedUserId) {
      promises.push(
        Api.Auth.updateCustomer(
          request,
          authHeaders,
          authenticatedUserId,
          orderSummary.customer,
        ),
      );
    } else {
      persistentSession.set("guestUser", orderSummary.customer);
      persistentSessionModified = true;
    }
  }

  // If site has changed, store "prevSite"
  const prevSite = persistentSession.get("prevSite");
  if (!prevSite || prevSite !== params.siteId) {
    persistentSession.set("prevSite", params.siteId);
    persistentSessionModified = true;
  }

  const errorOnInvalidAsset = getOptionalBoolFormValue(
    formData,
    "errorOnInvalidAsset",
  );

  // Prevent insert/update when specified in form
  const preventInsert = getOptionalBoolFormValue(formData, "preventInsert");
  const preventUpdate = getOptionalBoolFormValue(formData, "preventUpdate");

  try {
    const order = cartToReq(orderSummary);
    let resOrder: ResponseOrderWithLines | null = null;

    if (!orderId) {
      if (!preventInsert) {
        resOrder = await Api.Order.addOrder(
          authHeaders,
          orgId,
          params.siteId,
          order,
          errorOnInvalidAsset,
        );
        session.set("orderId", resOrder.id);
        sessionModified = true;
      }
    } else {
      if (!preventUpdate) {
        try {
          resOrder = await Api.Order.updateOrder(
            authHeaders,
            orgId,
            params.siteId,
            orderId,
            order,
            errorOnInvalidAsset,
          );
        } catch (e: any) {
          // Update failed due to the order not being found, we will fall back to adding instead.
          if (e.status === 404 && !preventInsert) {
            resOrder = await Api.Order.addOrder(
              authHeaders,
              orgId,
              params.siteId,
              order,
              errorOnInvalidAsset,
            );
            session.set("orderId", resOrder.id);
            sessionModified = true;
          } else {
            throw e;
          }
        }
      }
    }

    // If promo code was removed due to being longer valid redirect to show that error
    // Don't do this when currently trying to redeem a promo code / asset (in which case errorOnInvalidAsset will be set)
    if (resOrder && !errorOnInvalidAsset) {
      if (
        (order.redemptionKey && !resOrder.redemptionKey) ||
        (order.promoCode && !resOrder.promoCode)
      ) {
        // Redirect to show the error modal for error messages we have configured to show a user message
        const url = `/${params.orderSource}/${params.siteId}${
          params.table ? "/" + params.table : ""
        }?errorType=${
          order.redemptionKey && !resOrder.redemptionKey
            ? ErrorType.ASSET_AUTO_REMOVED
            : ErrorType.PROMO_CODE_AUTO_REMOVED
        }`;

        return redirect(url, {
          headers: await makeResponseHeaders(),
        });
      }
    }

    await Promise.all(promises);

    if (formData.get("redirectToPayment")) {
      return await redirectToPayment();
    }

    return json(
      { success: true, orderId, resOrder },
      { headers: await makeResponseHeaders() },
    );
  } catch (error: any) {
    // Prefer userMessage, which is the human readable message from the backend
    // (error.message is often the machine readable errorType, e.g. CODE_REDEMPTION_FAILED)
    const message =
      error?.userMessage ||
      error?.message ||
      "An error occurred while processing your order";
    if (
      error.errorType === "CODE_REDEMPTION_FAILED" ||
      error.errorType === "ASSET_REDEMPTION_FAILED"
    ) {
      return json(
        {
          success: false,
          errorType: error.errorType,
          message,
        },
        { status: 200 },
      );
    } else if (error.errorType && isErrorType(error.errorType)) {
      // Redirect to show the error modal for error messages we have configured to show a user message
      const url = `/${params.orderSource}/${params.siteId}${
        params.table ? "/" + params.table : ""
      }?errorType=${error.errorType}`;

      return redirect(url, {
        headers: await makeResponseHeaders(),
      });
    } else {
      throw json(
        { error: message },
        {
          status: 400,
          headers: await makeResponseHeaders(),
        },
      );
    }
  }
};

export const clientAction: ClientActionFunction = async ({
  request,
  serverAction,
}) => {
  const url = new URL(request.url);

  if (url.searchParams.get("clearCache") === "true") {
    clearLoaderCache();
  }
  const res: any = await serverAction();

  if (cachedLoaderData && res.resOrder) {
    cachedLoaderData.orderInfo = res.resOrder;
  }

  return res;
};

export async function loader({ params, request }: LoaderFunctionArgs) {
  const session = await getSession(request.headers.get("Cookie"));
  const persistentSession = await getPersistentSession(
    request.headers.get("Cookie"),
  );

  const existingOrderId = session.get("orderId");

  invariant(params.siteId, "expected params.siteId");
  invariant(params.orderSource, "expected params.orderSource");

  const url = new URL(request.url);
  const orderTypeCodeParam = url.searchParams.get("orderTypeCode");
  let menuCode = url.searchParams.get("menuCode");

  const siteInfo = await Api.Site.getSite(request, params.siteId);

  const orderSource = urlOrderSource[params.orderSource];

  let orderTypeCode = session.get("orderTypeCode");

  if (orderTypeCodeParam) {
    session.set("orderTypeCode", orderTypeCodeParam);
    orderTypeCode = orderTypeCodeParam;
  }

  if (
    !orderTypeCode ||
    !orderTypeCodeValid(siteInfo.site, orderSource, orderTypeCode)
  ) {
    orderTypeCode = getOrderTypeCode(orderSource, siteInfo.site.orderTypes);
    session.set("orderTypeCode", orderTypeCode);
  }

  const [
    menus,
    menu,
    currency,
    taxes,
    orderInfo,
    availableTimeSlots,
    orderAsapWait,
    banners,
  ] = await Promise.all([
    Api.Menu.getMenus(siteInfo.orgId, siteInfo.site.id, orderTypeCode),
    menuCode
      ? Api.Menu.getMenu(
          siteInfo.orgId,
          siteInfo.site.id,
          menuCode,
          orderTypeCode,
        )
      : null,
    Api.Currency.getCurrency(siteInfo.orgId, siteInfo.site.id),
    Api.Tax.getTaxes(siteInfo.orgId, siteInfo.site.id),
    existingOrderId
      ? Api.Order.tryGetOrder(siteInfo.orgId, siteInfo.site.id, existingOrderId)
      : null,
    Api.Site.getSiteTimeSlots(siteInfo.orgId, siteInfo.site.id, orderTypeCode),
    existingOrderId
      ? Api.Site.getAsapWaitForOrder(
          siteInfo.orgId,
          siteInfo.site.id,
          orderTypeCode,
          existingOrderId,
        ).then((x) => x?.asapWait)
      : null,
    // Banners are marketing content - never fail the page over them
    Api.Banners.getBanners(request, siteInfo.site.id, orderTypeCode).catch(
      (): OnlineBanner[] => [],
    ),
  ]);

  // There are various reasons we might want to force the user's cart to be clear (e.g. on payment completion)
  // however we still want to provide the details of the previous state of the order (e.g. for the payment completion screen)
  let clearOrder = false;

  // If the order is complete, show a clear order
  if (orderInfo && orderInfo.status !== OrderStatus.DRAFT) {
    clearOrder = true;
  }

  // If order type no longer matches previous order, show a clear order
  if (!clearOrder && orderInfo && orderInfo?.orderTypeCode !== orderTypeCode) {
    clearOrder = true;
  }

  // If we have an order with at least one line, use this to determine menu if not in url
  // Also clear the order if any line doesn't match the current menu
  if (!clearOrder && orderInfo?.lines?.length) {
    if (!menuCode) {
      menuCode = orderInfo.lines[0].menuCode;
    }
    // Still check *all* lines match the menu code, even if the menu code came from the first line
    if (orderInfo.lines.some((x) => x.menuCode !== menuCode)) {
      clearOrder = true;
    }
  }

  let sessionRequestedTime = session.get("requestedReadyTime");
  // Ignore session requested time if it is in the past
  if (
    sessionRequestedTime &&
    isBefore(new Date(sessionRequestedTime), new Date())
  ) {
    sessionRequestedTime = null;
  }

  // Use the requested ready time from the order (if current order) or closest slot matching session time
  // Don't verify if the order time is correct or not, as if the user makes no more modifications to their order then they may still be able to use the now invalid slot
  // Do verify however that the session time is valid, and if not select closest slot instead
  const requestedReadyTime =
    !clearOrder && orderInfo
      ? orderInfo.requestedReadyTime
      : getBestMatchingSlot(availableTimeSlots.times, sessionRequestedTime);

  // If the menu wasn't specified on the url / existing order, then get the first active menu according to the requested ready time
  // Note that this will only happen if the order doesn't have any lines, hence it is okay that we are calculating this after checking the lines match the selected menu
  if (!menuCode) {
    menuCode =
      getActiveMenu(menus, requestedReadyTime, siteInfo.site.timeZone)?.code ??
      null;
  }

  // If there was no menu setup throw an error
  if (!menuCode) {
    throw json({ message: "No menus found" }, { status: 500 });
  }

  // Fetch the current menu if not already fetched
  const loadedMenu =
    menu ||
    (await Api.Menu.getMenu(
      siteInfo.orgId,
      siteInfo.site.id,
      menuCode,
      orderTypeCode,
    ));

  const deliveryInfo = session.get("deliveryInfo") || null;

  return json(
    {
      siteInfo,
      siteId: params.siteId,
      banners,
      menus,
      menu: loadedMenu,
      currency,
      taxes,
      clearOrder,
      orderInfo,
      sessionRequestedTime,
      requestedReadyTime: requestedReadyTime,
      availableTimeSlots: availableTimeSlots.times,
      asapWait: orderAsapWait ?? availableTimeSlots.asapWait,
      tableNumber: orderInfo?.tableNumber ?? null,
      guestUser: persistentSession.get("guestUser"),
      orderTypeCode,
      asapAvailable: availableTimeSlots.asapWait != null,
      deliveryInfo,
    },
    {
      headers: {
        "Set-Cookie": await commitSession(session),
      },
    },
  );
}

let cachedLoaderData: SerializeFrom<typeof loader> | null = null;

export const clearLoaderCache = () => {
  cachedLoaderData = null;
};

export async function clientLoader({
  params,
  request,
  serverLoader,
}: ClientLoaderFunctionArgs) {
  invariant(params.siteId, "expected params.siteId");

  const url = new URL(request.url);
  let menuCode = url.searchParams.get("menuCode");
  const errorType = url.searchParams.get("errorType");
  const orderIdParam = url.searchParams.get("orderId");
  const orderTypeCodeParam = url.searchParams.get("orderTypeCode");

  // Force server loader to run if site / menu has changed or there was a handled api error
  // We force it to load on handled api error so that any out of date info displayed (such as whether a service is online or not) is corrected
  if (
    (orderTypeCodeParam &&
      orderTypeCodeParam !== cachedLoaderData?.orderTypeCode) ||
    !cachedLoaderData ||
    cachedLoaderData.siteId !== params.siteId ||
    (orderIdParam && cachedLoaderData?.orderInfo?.id !== orderIdParam) ||
    (menuCode != null && cachedLoaderData.menu.code !== menuCode) ||
    errorType
  ) {
    cachedLoaderData = await serverLoader();
    return cachedLoaderData;
  }

  // In client loader we will always return cached menu data and order type info
  // (changing order type requires clearing cache)
  menuCode = cachedLoaderData.menu.code;

  const [orderInfo, asapWait]: [ResponseOrderWithLines | null, number | null] =
    await Promise.all([
      cachedLoaderData.orderInfo?.id
        ? Api.Order.tryGetOrder(
            cachedLoaderData.siteInfo.orgId,
            cachedLoaderData.siteInfo.site.id,
            cachedLoaderData.orderInfo?.id,
          )
        : null,
      cachedLoaderData.requestedReadyTime != null
        ? cachedLoaderData.asapWait
        : cachedLoaderData.orderInfo?.id
        ? Api.Site.getAsapWaitForOrder(
            cachedLoaderData.siteInfo.orgId,
            cachedLoaderData.siteInfo.site.id,
            cachedLoaderData.orderTypeCode,
            cachedLoaderData.orderInfo.id,
          )
            .then(
              (x) =>
                x ||
                Api.Site.getAsapWait(
                  cachedLoaderData!.siteInfo.orgId,
                  cachedLoaderData!.siteInfo.site.id,
                  cachedLoaderData!.orderTypeCode,
                ),
            )
            .then((x) => x.asapWait)
        : Api.Site.getAsapWait(
            cachedLoaderData.siteInfo.orgId,
            cachedLoaderData.siteInfo.site.id,
            cachedLoaderData.orderTypeCode,
          ).then((x) => x.asapWait),
    ]);

  // There are various reasons we might want to force the user's cart to be clear (e.g. on payment completion)
  // however we still want to provide the details of the previous state of the order (e.g. for the payment completion screen)
  let clearOrder = false;

  // If the order is complete, show a clear order
  if (orderInfo && orderInfo.status !== OrderStatus.DRAFT) {
    clearOrder = true;
  }

  // If order type no longer matches previous order, show a clear order
  if (
    !clearOrder &&
    orderInfo &&
    orderInfo?.orderTypeCode !== cachedLoaderData.orderTypeCode
  ) {
    clearOrder = true;
  }

  // Clear the order if any line doesn't match the current menu
  if (!clearOrder && orderInfo?.lines?.some((x) => x.menuCode !== menuCode)) {
    clearOrder = true;
  }

  // Use the requested ready time from the order (if current order) or else from the server loader
  const requestedReadyTime =
    !clearOrder && orderInfo
      ? orderInfo.requestedReadyTime
      : cachedLoaderData.requestedReadyTime;

  return {
    ...cachedLoaderData,
    orderInfo,
    clearOrder,
    requestedReadyTime: requestedReadyTime,
    tableNumber: cachedLoaderData?.orderInfo?.tableNumber ?? null,
    asapWait,
  };
}
clientLoader.hydrate = true;

export const shouldRevalidate = ({
  actionResult,
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
  if (actionResult?.preventRevalidation) {
    return false;
  }
  // Force revalidation on navigation away from the payments page
  // This allows us to redirect back to the payments page if there is a payment in progress, or delete the payment otherwise
  if (isPaymentPage(currentUrl.pathname) && !isPaymentPage(nextUrl.pathname)) {
    return true;
  }
  return defaultShouldRevalidate;
};

export default function Page() {
  const pageRef = useRef<HTMLDivElement>(null);
  const loaderData = useLoaderData<typeof loader>();

  const location = useLocation();

  // Cancel payments when needed
  // Can't be done in loader as that doesn't handle navigation to parent routes,
  //   and is annoying to do in the clientLoader so would only work on page load
  const fetcher = useFetcher();
  useEffect(() => {
    if (
      fetcher.state === "idle" &&
      !isPaymentPage(location.pathname) &&
      !loaderData.clearOrder &&
      loaderData.orderInfo?.pendingPaymentIds.length
    ) {
      console.log("Cancelling payments");
      const formData = new FormData();
      formData.set("isCancelPayment", "true");
      formData.set("orgId", loaderData.siteInfo.orgId);
      formData.set(
        "paymentIds",
        JSON.stringify(loaderData.orderInfo?.pendingPaymentIds),
      );
      fetcher.submit(formData, {
        method: "post",
      });
    }
  }, [
    location.pathname,
    fetcher,
    loaderData.orderInfo?.pendingPaymentIds,
    loaderData.clearOrder,
    loaderData.siteInfo.orgId,
  ]);

  const { template } = useContext(ThemeContext);
  const { authenticatedUser } = useContext(AuthContext);

  const savedCustomer = authenticatedUser || loaderData.guestUser;

  const params = useParams();
  invariant(params.orderSource, "expected params.orderSource");
  const orderSource = urlOrderSource[params.orderSource];

  // Styles
  const backgroundColor = template.menuScreen.general.backgroundColour;

  const { menuOpen, serviceAvailable } = getServiceStatus(
    loaderData.siteInfo.site,
    loaderData.menu,
    orderSource,
    loaderData.requestedReadyTime,
  );

  const isOffline =
    !!loaderData.siteInfo.site &&
    (!menuOpen ||
      !serviceAvailable ||
      loaderData.availableTimeSlots?.length === 0);

  const signBackInRequired =
    !!loaderData?.orderInfo &&
    !loaderData?.clearOrder &&
    loaderData?.orderInfo?.customerId != null &&
    loaderData?.orderInfo?.customerId !== authenticatedUser?.id;

  return (
    <SiteContextProvider siteData={loaderData.siteInfo}>
      <TaxContextProvider
        taxes={loaderData.taxes}
        currency={loaderData.currency}
      >
        <MenuContextProvider menus={loaderData.menus} menu={loaderData.menu}>
          <BannerContextProvider banners={loaderData.banners}>
            <OrderContextProvider
              savedCustomer={savedCustomer}
              asapAvailable={loaderData.asapAvailable}
              orderInfo={loaderData.orderInfo}
              clearOrder={loaderData.clearOrder}
              orderSource={orderSource}
              orderTypeCode={loaderData.orderTypeCode}
              deliveryInfo={loaderData.deliveryInfo}
              paramOrderSource={params.orderSource}
              paramMenuCode={params.menuCode}
              availableTimeSlots={loaderData.availableTimeSlots}
              requestedReadyTime={loaderData.requestedReadyTime}
              asapWait={loaderData.asapWait}
              tableNumber={loaderData.tableNumber}
              timeValid={!isOffline}
              signBackInRequired={signBackInRequired}
              sessionRequestedTime={loaderData.sessionRequestedTime}
            >
              <PaymentContextProvider>
                <BaseLayout
                  orgId={loaderData.siteInfo.orgId}
                  menus={loaderData.menus}
                  menu={loaderData.menu}
                  site={loaderData.siteInfo.site}
                  showOffline={isOffline}
                >
                  <PageWrapper
                    pageRef={pageRef}
                    loading={false}
                    style={{ backgroundColor, padding: 0 }}
                  >
                    <SignBackInModal
                      visible={signBackInRequired}
                      wrongAccount={
                        signBackInRequired && !!authenticatedUser?.id
                      }
                    />
                    <Outlet />
                  </PageWrapper>
                </BaseLayout>
              </PaymentContextProvider>
            </OrderContextProvider>
          </BannerContextProvider>
        </MenuContextProvider>
      </TaxContextProvider>
    </SiteContextProvider>
  );
}

export const ErrorBoundary = DefaultErrorBoundary;
